import { createContext, useContext } from 'react';
import { hf, hfAlpha, isDarkTearsheetSkin, tokensForMode, type HfThemeMode, type TearsheetTheme } from '../../theme';

/** Colors HFChart reads when rendering (engine-agnostic). */
export type HFChartPalette = {
  series: string[];
  categorical: string[];
  long: string;
  short: string;
  /** Gain / positive signed values (bars colored by sign). */
  positive: string;
  /** Loss / negative signed values (bars colored by sign). */
  negative: string;
  gridColor: string;
  axisLabelColor: string;
  legendText: string;
};

/** Multi-hue ramp from brand tokens only (no ad-hoc hex). */
export function categoricalColors(mode: HfThemeMode): string[] {
  const tokens = tokensForMode(mode);
  const dark = mode === 'dark';
  return [
    dark ? hf.blue300 : hf.blue500,
    hf.cyan300,
    tokens.positive,
    tokens.negative,
    dark ? hf.slate600 : hf.navy500,
    dark ? hf.gray300 : hf.gray700,
    hf.blue400,
    dark ? hf.coolGray200 : hf.gray400,
  ];
}

export function chartPalette(mode: HfThemeMode): HFChartPalette {
  const tokens = tokensForMode(mode);
  const dark = mode === 'dark';

  return {
    series: [
      dark ? hf.blue300 : hf.blue500,
      dark ? hf.cyan300 : hf.blue400,
      dark ? hf.gray300 : hf.navy500,
      dark ? hf.slate600 : hf.gray700,
      dark ? hf.blue400 : hf.cyan300,
      dark ? hf.coolGray200 : hf.gray400,
    ],
    categorical: categoricalColors(mode),
    long: dark ? hf.slate600 : hf.navy700,
    short: hf.cyan300,
    positive: tokens.positive,
    negative: tokens.negative,
    gridColor: dark ? hfAlpha(hf.white, 0.14) : hfAlpha(hf.navy900, 0.1),
    axisLabelColor: tokens.muted,
    legendText: tokens.text,
  };
}

function seriesForSkinColors(skin: TearsheetTheme, dark: boolean): string[] {
  const tokens = tokensForMode(dark ? 'dark' : 'light');
  return [dark ? hf.blue300 : hf.blue500, skin.accent, tokens.positive, skin.muted, skin.border, skin.text];
}

/** Chart colors for a tearsheet skin (skin tokens + brand `hf`). */
export function chartPaletteFromSkinColors(skin: TearsheetTheme): HFChartPalette {
  const dark = isDarkTearsheetSkin(skin);
  const tokens = tokensForMode(dark ? 'dark' : 'light');
  const base = chartPalette(dark ? 'dark' : 'light');

  return {
    ...base,
    series: seriesForSkinColors(skin, dark),
    categorical: [skin.accent, ...base.categorical],
    long: tokens.positive,
    short: tokens.negative,
    positive: tokens.positive,
    negative: tokens.negative,
    gridColor: skin.border,
    axisLabelColor: skin.muted,
    legendText: skin.text,
  };
}

/** Optional override (tearsheet skins). Null = use `chartPalette(mode)`. */
export const HFChartPaletteContext = createContext<HFChartPalette | null>(null);

export function useHFChartPaletteOverride(): HFChartPalette | null {
  return useContext(HFChartPaletteContext);
}
